import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { IntegerInput } from '@shared-ui/components';
import { adminApi } from '../../api/adminApi';
import type { AppSettings, RateLimitConfig } from '../../types/settings';
import './RateLimits.css';

const DEFAULT_RATE_LIMITS: RateLimitConfig = {
  student: { apiPerMinute: 60, submissionsPerHour: 60, llmPerDay: 100 },
  teacher: { apiPerMinute: 60, submissionsPerHour: 60, llmPerDay: 100 },
  admin:   { apiPerMinute: 120, submissionsPerHour: 120, llmPerDay: 200 },
};

export function RateLimits() {
  const queryClient = useQueryClient();
  const [rateLimits, setRateLimits] = useState<RateLimitConfig>(DEFAULT_RATE_LIMITS);
  const [rateLimitsSaveStatus, setRateLimitsSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: adminApi.getSettings,
  });

  useEffect(() => {
    if (settings?.rateLimits) {
      setRateLimits(settings.rateLimits);
    }
  }, [settings]);

  const mutation = useMutation({
    mutationFn: (newSettings: Partial<AppSettings>) => adminApi.updateSettings(newSettings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });

  return (
    <div className="settings-section">
      <div className="section-header">
        <h2>Rate Limits</h2>
        <p className="section-description">
          Configure default rate limits per user type. Changes apply to all users immediately (cached for up to 5 minutes on the backend).
        </p>
      </div>

      {(['student', 'teacher', 'admin'] as const).map(userType => {
        const tier = rateLimits[userType];
        return (
          <div key={userType} className="rate-limit-tier">
            <h3 className="rate-limit-tier-title">{userType}</h3>
            <div className="rate-limit-fields">
              <div className="rate-limit-field">
                <label className="rate-limit-label">API requests / minute</label>
                <IntegerInput
                  className="rate-limit-input"
                  min={1}
                  max={1000}
                  value={tier.apiPerMinute}
                  onChange={(val) => {
                    if (val == null) return;
                    setRateLimits(prev => ({ ...prev, [userType]: { ...prev[userType], apiPerMinute: val } }));
                  }}
                />
              </div>
              <div className="rate-limit-field">
                <label className="rate-limit-label">Submissions / hour</label>
                <IntegerInput
                  className="rate-limit-input"
                  min={1}
                  max={1000}
                  value={tier.submissionsPerHour}
                  onChange={(val) => {
                    if (val == null) return;
                    setRateLimits(prev => ({ ...prev, [userType]: { ...prev[userType], submissionsPerHour: val } }));
                  }}
                />
              </div>
              <div className="rate-limit-field">
                <label className="rate-limit-label">LLM calls / day</label>
                <IntegerInput
                  className="rate-limit-input"
                  min={1}
                  max={10000}
                  value={tier.llmPerDay}
                  onChange={(val) => {
                    if (val == null) return;
                    setRateLimits(prev => ({ ...prev, [userType]: { ...prev[userType], llmPerDay: val } }));
                  }}
                />
              </div>
            </div>
          </div>
        );
      })}

      <div className="rate-limit-actions">
        <button
          className="rate-limit-save-btn"
          onClick={() => {
            setRateLimitsSaveStatus('saving');
            mutation.mutate({ rateLimits }, {
              onSuccess: () => {
                setRateLimitsSaveStatus('saved');
                setTimeout(() => setRateLimitsSaveStatus('idle'), 2000);
              },
              onError: () => {
                setRateLimitsSaveStatus('error');
                setTimeout(() => setRateLimitsSaveStatus('idle'), 3000);
              },
            });
          }}
          disabled={mutation.isPending}
        >
          {mutation.isPending && rateLimitsSaveStatus === 'saving' ? 'Saving...' : 'Save Rate Limits'}
        </button>
        <button
          className="rate-limit-reset-btn"
          onClick={() => setRateLimits(DEFAULT_RATE_LIMITS)}
        >
          Reset to Defaults
        </button>
        {rateLimitsSaveStatus === 'saved' && <span className="status-saved">Rate limits saved!</span>}
        {rateLimitsSaveStatus === 'error' && <span className="status-error">Failed to save</span>}
      </div>
    </div>
  );
}
